import { useEffect, useRef, useState } from 'react';
import { Spinner } from './Spinner';

export interface LogQuoteFormValues {
  amount: number;
  service_label: string;
  vehicle?: string;
  send_recap: boolean;
}

interface LogQuoteModalProps {
  open: boolean;
  contactName: string;
  busy?: boolean;
  /** Optional preset chips for fast service selection (e.g. "Premium Detail",
   *  "Interior Detail", "Full Detail"). Tapping a chip fills the service
   *  input but the owner can still edit free-text. */
  servicePresets?: string[];
  onCancel: () => void;
  onConfirm: (values: LogQuoteFormValues) => void;
}

/**
 * Owner-side manual quote entry. Used for off-Shelby quotes — when the
 * owner picked up the call themselves (telco forward dipped through,
 * customer called the personal cell, walk-in, etc.) and wants to record
 * the quote so the contact card and reports reflect it.
 *
 * The "Also send recap text + start drip" checkbox is default ON. When
 * checked the Worker fires the v2 recap workflow + drip enrollment,
 * exactly matching Shelby's analyzer path — so a quote logged here gets
 * the same automated follow-up cadence as a Shelby-handled quote. Owner
 * can uncheck for cases like Lori where the customer specifically asked
 * not to be texted (call only).
 */
export function LogQuoteModal({
  open,
  contactName,
  busy = false,
  servicePresets,
  onCancel,
  onConfirm,
}: LogQuoteModalProps) {
  const [amount, setAmount] = useState<string>('');
  const [serviceLabel, setServiceLabel] = useState<string>('');
  const [vehicle, setVehicle] = useState<string>('');
  // 2026-06-01 — default OFF (was ON). Owner-logged quotes from off-Shelby
  // calls are usually after a real human conversation; the customer doesn't
  // need an automated recap + drip on top. Owner explicitly opts in per-quote
  // when they want it (e.g., to set the v2 quote-recap workflow in motion).
  const [sendRecap, setSendRecap] = useState<boolean>(false);
  const [touched, setTouched] = useState<boolean>(false);

  const amountInputRef = useRef<HTMLInputElement | null>(null);

  // Reset form on open so a previous abandoned modal doesn't bleed in.
  useEffect(() => {
    if (open) {
      setAmount('');
      setServiceLabel('');
      setVehicle('');
      setSendRecap(false);
      setTouched(false);
      // Auto-focus amount on open.
      window.setTimeout(() => amountInputRef.current?.focus(), 40);
    }
  }, [open]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const parsedAmount = Number.parseFloat(amount.replace(/[^0-9.]/g, ''));
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const serviceValid = serviceLabel.trim().length > 0;
  const canSubmit = amountValid && serviceValid && !busy;

  const handleSubmit = () => {
    setTouched(true);
    if (!canSubmit) return;
    onConfirm({
      amount: parsedAmount,
      service_label: serviceLabel.trim(),
      vehicle: vehicle.trim() ? vehicle.trim() : undefined,
      send_recap: sendRecap,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="log-quote-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4 backdrop-blur-sm"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        className="w-full max-w-sm rounded-ss-lg bg-white p-5 shadow-ss-md dark:bg-[#1E293B]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="log-quote-dialog-title"
          className="text-base font-bold text-ink-900 dark:text-white"
        >
          Log quote for {contactName}
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-ink-600 dark:text-[#94A3B8]">
          Use this when you quoted them yourself (off-Shelby). Saved to their
          contact card the same way Shelby would.
        </p>

        {/* Amount */}
        <label className="mt-4 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Quoted amount
          </span>
          <div className="relative mt-1">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-500 dark:text-[#94A3B8]"
            >
              $
            </span>
            <input
              ref={amountInputRef}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              placeholder="399.99"
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-ss-sm border border-ink-200 bg-white py-2 pl-7 pr-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
            />
          </div>
          {touched && !amountValid ? (
            <span className="mt-1 block text-xs text-status-attention">
              Enter an amount greater than 0.
            </span>
          ) : null}
        </label>

        {/* Service */}
        <label className="mt-3 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Service
          </span>
          <input
            type="text"
            autoComplete="off"
            value={serviceLabel}
            placeholder="Premium Detail"
            onChange={(e) => setServiceLabel(e.target.value)}
            className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
          />
          {servicePresets && servicePresets.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {servicePresets.map((preset) => {
                const active = serviceLabel === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setServiceLabel(preset)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      active
                        ? 'border-ss-blue bg-ss-blue text-white'
                        : 'border-ink-200 bg-ink-50 text-ink-700 hover:bg-ink-100 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#CBD5E1] dark:hover:bg-[#334155]'
                    }`}
                  >
                    {preset}
                  </button>
                );
              })}
            </div>
          ) : null}
          {touched && !serviceValid ? (
            <span className="mt-1 block text-xs text-status-attention">
              Pick or type the service you quoted.
            </span>
          ) : null}
        </label>

        {/* Vehicle (optional) */}
        <label className="mt-3 block">
          <span className="text-xs font-semibold text-ink-700 dark:text-[#CBD5E1]">
            Vehicle <span className="font-normal text-ink-400">(optional)</span>
          </span>
          <input
            type="text"
            autoComplete="off"
            value={vehicle}
            placeholder="2018 Jeep Wrangler"
            onChange={(e) => setVehicle(e.target.value)}
            className="mt-1 w-full rounded-ss-sm border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none focus:ring-2 focus:ring-ss-blue/20 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
          />
        </label>

        {/* Send recap toggle */}
        <label className="mt-4 flex items-start gap-2.5 rounded-ss-sm border border-ink-200 bg-ink-50 px-3 py-2.5 dark:border-[#334155] dark:bg-[#0F172A]">
          <input
            type="checkbox"
            checked={sendRecap}
            onChange={(e) => setSendRecap(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-ss-blue"
          />
          <span className="text-xs leading-relaxed text-ink-700 dark:text-[#CBD5E1]">
            <span className="font-semibold">Also send recap text + start drip</span>
            <br />
            <span className="text-ink-500 dark:text-[#94A3B8]">
              Uncheck if the customer asked not to be texted (call only).
            </span>
          </span>
        </label>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-ss-sm border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#CBD5E1] dark:hover:bg-[#334155]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-ss-sm bg-ss-blue px-4 py-2 text-sm font-semibold text-white hover:bg-ss-blue-dark disabled:opacity-70"
          >
            {busy ? (
              <>
                <Spinner />
                <span>Saving…</span>
              </>
            ) : (
              'Log quote'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
